import { Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  bottomMenuItems,
  rentalOverviewItem,
  rentalPropertiesItem,
} from 'src/shared/components/bottom-nav/bottom-nav-items';
import { NavController } from '@ionic/angular';

import { TranslationService } from 'src/tidy-ui-components/providers/translation.service';
import { Events } from 'src/providers/events/events';
import { propertyDashboardPath } from 'src/providers/guard/rental-routes.guard';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { DemoTourService } from 'src/shared/services/demo-tour.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';

@Component({
    selector: 'tidy-bottom-nav',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./bottom-nav.component.scss'],
    template: `
    @if (showDemoBar) {
      <div class="demo-mobile-bar">
        <span class="dmb-sparkle material-icons">auto_awesome</span>
        <span class="dmb-label">Viewing Demo Account</span>
        <button class="dp-btn dp-btn-white" (click)="startDemoOnboarding()">{{ demoIndicatorLabel }}</button>
      </div>
    } @else if (onboardingNudgeLabel) {
      <button class="onboarding-mobile-bar" (click)="openOnboardingPill()">
        <img [src]="managerPhoto" class="omb-avatar" alt="Account Manager" />
        <span class="omb-dot"></span>
        <span class="omb-label">{{ onboardingNudgeLabel | translate }}</span>
        <span class="omb-chevron material-icons">chevron_right</span>
      </button>
    }
    <nav>
      <ul class="menu">
        @for (item of menuItems; track item) {
          <li
            class="menu-item"
            [class.active-item]="isActiveItem(item)"
            [class.chat-item]="item.label === 'Chat'"
            [attr.data-cy]="item.label"
            [attr.data-testid]="'bottom-nav-' + item.target"
            (click)="navigateTo(item.target)">
            @if (item.label === 'Chat') {
              <tidy-ai-loader [class]="'title-size no-right-margin'" [colored]="isActiveItem(item)" />
            } @else {
              <span
                class="bottom-nav-icon"
                [class.bottom-nav-icon--active]="isActiveItem(item)"
                [style.-webkit-mask-image]="'url(' + item.icon + ')'"
                [style.mask-image]="'url(' + item.icon + ')'">
              </span>
            }
            <span class="item-menu" [class.active]="isActiveItem(item)">
              {{item.label | translate}}
            </span>
          </li>
        }
      </ul>
    </nav>
    `,
    standalone: false
})
export class BottomNavComponent {

  @Input() activeRoute: string;
  menuItems: typeof bottomMenuItems;
  // Account manager avatar on the onboarding nudge bar (from /me; default until assigned).
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;
  private translationService = inject(TranslationService);
  private demoAccount = inject(DemoAccountService);
  private demoTour = inject(DemoTourService);
  private accountManagerService = inject(AccountManagerService);

  // Demo-mode bar (mobile): a blue band attached above the bottom nav, shown only while
  // browsing the shared demo account, with the same "Get Started" nudge as the desktop pill.
  // Hidden while the guided tour's dock occupies the bottom of the screen.
  get showDemoBar(): boolean {
    return this.demoAccount.isDemoSession()
      && this.demoAccount.entrancePhase() === 'idle'
      && !this.demoTour.active();
  }

  // pm-schedule-call-first: demo-bar button label, computed in DemoAccountService so it stays in
  // lockstep with the desktop pill ("Get Started" for control; "Schedule Call"/"Create Account").
  get demoIndicatorLabel(): string {
    return this.demoAccount.demoIndicatorCta().label;
  }

  // Hand off to app.component (which owns the right-side-panel flow) so this leaf nav
  // component doesn't need to import the Get Started wizard. app.component recomputes the
  // target startStep from demoIndicatorCta(), so the event needs no payload.
  startDemoOnboarding(): void {
    this.events.publish('demo:get-started');
  }

  // Onboarding nudge (mobile): a slim bar attached above the bottom nav for converted rental
  // accounts. Shows the live pill's single call-out — "Schedule Setup Call" until the call is
  // booked, then one outstanding action item — or nothing when there's nothing to surface.
  // The label is computed in DemoAccountService alongside the desktop pill so the two agree.
  get onboardingNudgeLabel(): string | null {
    // The More page renders this same indicator at the top of its own content, so don't
    // double it up with the bar here.
    if (this.activeRoute === '/more') { return null; }
    // On the properties page the "Add a Property" card (with social proof) would overlap the
    // bar while the user has no property — hide it there until they add one. Every other page
    // shows it regardless.
    if (this.activeRoute === propertyDashboardPath() && !this.demoAccount.onboardingNudgeHasProperty()) {
      return null;
    }
    return this.demoAccount.onboardingNudgeLabel();
  }

  // Hand off to app.component so this leaf nav component doesn't import the wizard or the
  // onboarding-status page; it lands on the same screen the More-page indicator does.
  openOnboardingPill(): void {
    this.events.publish('onboarding:open-pill');
  }

  constructor(
    private customNavController: NavController,
    private events: Events,
  ) {
    this.rebuildMenu();
    this.accountManagerService.getDisplay().then((manager) => {
      this.managerPhoto = manager.photoUrl;
    });

    // The bar layout depends on the account type — rebuild whenever the
    // auth modal flow lands or the user picks an account type, since the
    // component instance survives those events but `isRentalClient` in
    // localStorage may have just flipped.
    this.events.subscribe('auth:complete', () => this.rebuildMenu());
    this.events.subscribe('auth:type-selected', () => this.rebuildMenu());

    this.translationService.languageChange.pipe(
      takeUntilDestroyed()
    ).subscribe(() => {
      // Re-clone the active layout so translated labels refresh.
      this.rebuildMenu();
    });
  }

  /**
   * Rental clients get a P&L-focused bottom nav:
   *   Chat → Overview (/overview) → Schedule → Properties (/properties) → More
   * Everyone else keeps the develop layout:
   *   Chat → Overview (/overview) → Schedule → Messages → More
   * Anything not in the bar lives in the More page.
   */
  private rebuildMenu(): void {
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.menuItems = isRentalClient
      ? [
          bottomMenuItems[0],     // Chat        → /chat
          rentalOverviewItem,     // Overview    → /overview
          bottomMenuItems[4],     // Schedule    → /schedule
          rentalPropertiesItem,   // Properties  → /properties
          bottomMenuItems[6],     // More        → /more
        ]
      : [
          bottomMenuItems[0],     // Chat        → /chat
          bottomMenuItems[1],     // Overview    → /overview
          bottomMenuItems[4],     // Schedule    → /schedule
          bottomMenuItems[5],     // Messages    → /messages
          bottomMenuItems[6],     // More        → /more
        ];
  }

  async navigateTo(url) {
    if (url === '/schedule') {
      const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      const shouldShowAllToDosPage = isRentalClient;
      localStorage.setItem('shouldShowAllToDosPage', shouldShowAllToDosPage ? 'true' : 'false');
      url = shouldShowAllToDosPage ? '/schedule' : '/schedule-list';
    }
    this.customNavController.navigateForward(url);
  }

  isActiveItem(item) {
    return this.activeRoute === item.target;
  }
}
